import React from 'react';
import {
  FunctionField,
  Loading,
  RecordContextProvider,
  useGetOne,
  useRecordContext,
} from 'react-admin';
import { Grid } from '@mui/material';

import auditTrackDiffGenerator, { AuditTrack } from '../audit/auditDiffGenerator/auditTrackDiffGenerator';
import AuditExtraInfo from '../audit/AuditExtraInfo';
import NoResults from '../layout/NoResults';
import TextDiffField from '../utils/auditTracks/TextDiffField';
import DateDiffField from '../utils/auditTracks/DateDiffField';
import StatusDiffField from '../utils/auditTracks/StatusDiffField';
import ReferenceDiffField from '../utils/auditTracks/ReferenceDiffField';
import resourceNames from '../../constants/resourceNames';

type IReview = {
  action: string;
  status: string;
  reasoning: string;
  comment: string;
  submittedAt: string;
  resolvedAt: string;
  actionInformation: {
    description: string;
    ruleType: string;
    ruleVersion: string;
  };
};

const ReviewAuditDetails = () => {
  const record = useRecordContext<AuditTrack<IReview>>();
  const { data, isLoading } = useGetOne<AuditTrack<IReview>>(
    `audit-tracks/${record?.id}/previous`,
    { id: '' },
    { enabled: !!record },
  );

  if (!record) return <NoResults />;

  if (!data) {
    if (isLoading) return <Loading />;
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const isElementCreated = data.id === record.id;

  if (!record.documentAfter || (!isElementCreated && !data.documentAfter)) {
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const auditTracksDiff = auditTrackDiffGenerator<IReview>({
    current: record,
    previous: !isElementCreated ? data : undefined,
  });

  const { resourceData } = auditTracksDiff;

  const hasFieldChanges = {
    action: resourceData.action?.hasChanged,
    status: resourceData.status?.hasChanged,
    reasoning: resourceData.reasoning?.hasChanged,
    comment: resourceData.comment?.hasChanged,
    submittedAt: resourceData.submittedAt?.hasChanged,
    resolvedAt: resourceData.resolvedAt?.hasChanged,
    actionInformationDescription: resourceData.actionInformation?.description?.hasChanged,
    actionInformationRuleType: resourceData.actionInformation?.ruleType?.hasChanged,
    actionInformationRuleVersion: resourceData.actionInformation?.ruleVersion?.hasChanged,
  };

  const hasAnyChanges = Object.values(hasFieldChanges).some((field) => field);

  return (
    <Grid container spacing={6}>
      {!hasAnyChanges && <NoResults text="Only system changes detected" />}
      {hasAnyChanges && (
        <RecordContextProvider value={resourceData}>
          {hasFieldChanges.action && <TextDiffField source="action" label="Action" />}
          {hasFieldChanges.status && <StatusDiffField source="status" label="Status" resource={resourceNames.REVIEWS} />}
          {hasFieldChanges.reasoning && <TextDiffField source="reasoning" label="Reasoning" />}
          {hasFieldChanges.comment && <TextDiffField source="comment" label="Comment" />}
          {hasFieldChanges.submittedAt && <DateDiffField source="submittedAt" label="Submitted at" showTime />}
          {hasFieldChanges.resolvedAt && <DateDiffField source="resolvedAt" label="Resolved at" showTime />}
          {hasFieldChanges.actionInformationDescription && (
            <TextDiffField source="actionInformation.description" label="Action description" />
          )}
          {hasFieldChanges.actionInformationRuleType && (
            <TextDiffField source="actionInformation.ruleType" label="Rule type" />
          )}
          {hasFieldChanges.actionInformationRuleVersion && (
            <ReferenceDiffField
              source="actionInformation.ruleVersion"
              label="Rule version"
              reference={resourceNames.RULE_VERSION}
            >
              <FunctionField render={(r: any) => (r ? `Version ${r.number}` : '-')} />
            </ReferenceDiffField>
          )}
        </RecordContextProvider>
      )}
      {record.extraInfo && <AuditExtraInfo />}
    </Grid>
  );
};

export default ReviewAuditDetails;
